import { z } from 'zod';
import { isoDateTimeSchema, localTimeSchema, uuidSchema } from './common.js';
import { presetStatusSchema } from './preset.js';
import { dayPartCodeSchema } from './preset-showcase.js';
import { tierAccessEnum } from './track.js';
/**
 * The tariffs a category schedule template is kept for (D-24). No `lite`: a
 * Lite venue has no schedule to seed.
 *
 * DERIVED from `tierAccessEnum` rather than written out, so the tariff
 * vocabulary keeps one home. `presetListFilterSchema.template_tariff`
 * (`preset.ts`) derives from the same enum for the same reason — importing this
 * const there would close a `preset.ts` ↔ `venue-format.ts` cycle, and a cycle
 * in this package is a TDZ crash at import time, not a lint warning.
 */
export const venueFormatTemplateTariffSchema = tierAccessEnum.exclude(['lite']);
/**
 * Per-tariff «где ещё не сделано» column of the category list (design §19.17).
 * Served by one `GROUP BY (venue_format_id, tariff_code, part)` over the whole
 * table — ten categories, no N+1. The full template lives in the section at the
 * bottom of this file.
 */
export const venueFormatTemplateSummarySchema = z
    .object({
    /** e.g. `['morning','evening']` → «2/3». */
    filled_parts: z.array(dayPartCodeSchema),
    unavailable_count: z.number().int().nonnegative(),
})
    .strict();
/**
 * Typical day-part boundaries of a venue category (venue-category-discovery
 * arch §2.1, PRD §7.2). Three typed `time` columns rather than one jsonb blob:
 * `default_bpm_curve` is the jsonb column that silently drifted out of contract
 * for months (BUG-283), and a boundary document would repeat that.
 */
const PART_BOUNDARY_KEYS = [
    'default_morning_start',
    'default_day_start',
    'default_evening_start',
];
const PART_BOUNDARY_ALL_OR_NONE = 'Границы частей дня задаются целиком: либо все три, либо ни одной';
/**
 * Mirrors the DB CHECK `venue_formats_part_boundaries_valid` (migration 0115):
 * all three boundaries unset, or all three set and strictly increasing.
 *
 * WHY IT IS DUPLICATED IN ZOD. Without this the API answers a Postgres `23514`
 * — an internal error with no field to point at, which the project rule forbids
 * surfacing — instead of a 400 naming the offending boundary. The ordering half
 * also mirrors the ADR-028 guard (`morning < day < evening`), so a category can
 * never seed a schedule the part editor would reject.
 *
 * ON A PATCH the rule is stronger than the CHECK: a body that mentions ONE
 * boundary is refused even though the resulting row might be legal, because the
 * schema cannot see the other two columns. Half-set is the failure mode that
 * actually hides (design §12.2), and the admin editor always writes all three
 * — «Вернуть типовые» included.
 */
function refinePartBoundaries(value, ctx) {
    const mentioned = PART_BOUNDARY_KEYS.filter((key) => value[key] !== undefined);
    if (mentioned.length === 0)
        return;
    if (mentioned.length !== PART_BOUNDARY_KEYS.length) {
        for (const key of PART_BOUNDARY_KEYS) {
            if (value[key] === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: [key],
                    message: PART_BOUNDARY_ALL_OR_NONE,
                });
            }
        }
        return;
    }
    const cleared = PART_BOUNDARY_KEYS.filter((key) => value[key] === null);
    if (cleared.length === PART_BOUNDARY_KEYS.length)
        return;
    if (cleared.length > 0) {
        for (const key of cleared) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: PART_BOUNDARY_ALL_OR_NONE,
            });
        }
        return;
    }
    const morning = value.default_morning_start;
    const day = value.default_day_start;
    const evening = value.default_evening_start;
    if (!(morning < day)) {
        ctx.addIssue({
            code: 'custom',
            path: ['default_day_start'],
            message: 'Начало дня должно быть позже начала утра',
        });
    }
    if (!(day < evening)) {
        ctx.addIssue({
            code: 'custom',
            path: ['default_evening_start'],
            message: 'Начало вечера должно быть позже начала дня',
        });
    }
}
/**
 * Per-hour BPM range, used by `default_bpm_curve` (24 entries, hour 0..23).
 * `bpm_min` ≤ `bpm_max` is enforced at the array level via `superRefine`.
 */
export const venueFormatBpmCurveEntrySchema = z
    .object({
    hour: z.number().int().min(0).max(23),
    bpm_min: z.number().int().min(20).max(300),
    bpm_max: z.number().int().min(20).max(300),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.bpm_min > v.bpm_max) {
        ctx.addIssue({
            code: 'custom',
            path: ['bpm_min'],
            message: 'bpm_min must be ≤ bpm_max',
        });
    }
});
/**
 * Exactly 24 entries — one per local hour. Backend additionally checks that
 * the array covers each `hour ∈ 0..23` exactly once.
 */
export const venueFormatBpmCurveSchema = z
    .array(venueFormatBpmCurveEntrySchema)
    .length(24, 'default_bpm_curve must contain 24 entries (one per hour)')
    .superRefine((entries, ctx) => {
    const seen = new Set();
    for (const entry of entries) {
        if (seen.has(entry.hour)) {
            ctx.addIssue({
                code: 'custom',
                path: [],
                message: `default_bpm_curve has duplicate hour ${entry.hour}`,
            });
            return;
        }
        seen.add(entry.hour);
    }
    for (let h = 0; h < 24; h += 1) {
        if (!seen.has(h)) {
            ctx.addIssue({
                code: 'custom',
                path: [],
                message: `default_bpm_curve is missing hour ${h}`,
                // first missing wins — further checks would be noise
            });
            return;
        }
    }
});
/**
 * AI stream code — lowercase slug. Catalog is curated separately; backend
 * does not currently whitelist the values, so this is shape-only.
 */
export const venueFormatAiStreamCodeSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_-]*$/, 'Lowercase slug');
/**
 * Wire DTO for a single venue-format row.
 *
 * NOTE: the name `venueFormatSchema` is already taken by a tiny
 * `z.string()` slug schema in `./venue.ts` (the venue DTO's `format`
 * field). We export this richer row schema under `venueFormatRowSchema`
 * to avoid colliding at the barrel.
 */
export const venueFormatRowSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(255),
    code: z.string().min(1).max(64),
    default_bpm_curve: venueFormatBpmCurveSchema.nullable(),
    default_ai_streams: z.array(venueFormatAiStreamCodeSchema).nullable(),
    /**
     * Typical day-part boundaries (arch §2.1, PRD §7.2) — the seed the
     * auto-schedule falls back to when a venue has no working hours. All three
     * `null` means «не заданы», which is a legitimate state for a category
     * nobody has configured yet; the preview reports it as
     * `CATEGORY_BOUNDARIES_UNSET` rather than absorbing it silently.
     *
     * No cross-field refine on the ROW schema on purpose: it describes a server
     * RESPONSE, where the DB CHECK already guarantees the invariant, and a
     * `.refine()` would turn this object into a `ZodEffects` that can no longer
     * be `.extend()`ed by an admin consumer.
     */
    default_morning_start: localTimeSchema.nullable(),
    default_day_start: localTimeSchema.nullable(),
    default_evening_start: localTimeSchema.nullable(),
    /**
     * Curation knob — Wave 2.64 BE (TASK-095a-followup). Distinct from
     * soft-delete: `is_active=false` keeps the row in the catalog list but
     * hides it from venue-create dropdowns.
     */
    is_active: z.boolean(),
    /**
     * Per-tariff template fill state (PRD §13, design §19.17). Populated by
     * `list()` and `getById()`, omitted on create/patch — hence optional. The
     * template itself is fetched separately
     * (`GET /admin/settings/venue-formats/:id/schedule-template`).
     */
    schedule_template: z
        .object({
        standard: venueFormatTemplateSummarySchema,
        pro: venueFormatTemplateSummarySchema,
    })
        .strict()
        .optional(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
export const venueFormatListResponseSchema = z.object({
    items: z.array(venueFormatRowSchema),
});
export const venueFormatCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    code: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case'),
    default_bpm_curve: venueFormatBpmCurveSchema.optional(),
    default_ai_streams: z.array(venueFormatAiStreamCodeSchema).optional(),
    default_morning_start: localTimeSchema.nullable().optional(),
    default_day_start: localTimeSchema.nullable().optional(),
    default_evening_start: localTimeSchema.nullable().optional(),
    is_active: z.boolean().optional(),
})
    .strict()
    .superRefine(refinePartBoundaries);
export const venueFormatPatchSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    code: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case')
        .optional(),
    default_bpm_curve: venueFormatBpmCurveSchema.nullable().optional(),
    default_ai_streams: z
        .array(venueFormatAiStreamCodeSchema)
        .nullable()
        .optional(),
    default_morning_start: localTimeSchema.nullable().optional(),
    default_day_start: localTimeSchema.nullable().optional(),
    default_evening_start: localTimeSchema.nullable().optional(),
    is_active: z.boolean().optional(),
})
    .strict()
    .superRefine(refinePartBoundaries)
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
// ---------------------------------------------------------------------------
// Category schedule template (PRD §13, arch §3.5, ADR-039).
//
// The template lives HERE rather than in its own module because it is a
// property of a venue format — splitting it out would put the two halves of one
// editor in two modules. It carries ONLY the presets per day part (D-22); the
// part BOUNDARIES stay the three `default_*_start` fields above and are still
// adapted to the venue's working hours at proposal time.
// ---------------------------------------------------------------------------
/**
 * One position in a part's ordered sequence. Mirrors
 * `playerSchedulePartUpsertItemSchema` — the template writes the same shape the
 * player's own part editor writes, so a templated part and a hand-made one are
 * the same object downstream.
 */
export const venueFormatTemplateItemWriteSchema = z
    .object({
    preset_id: uuidSchema,
    /** `null` === «до конца слота». 15-minute multiples, ≥15 (D-4 step). */
    duration_minutes: z.number().int().min(15).multipleOf(15).nullable(),
})
    .strict();
/**
 * One part: at most 5 positions — the same cap the CLIENT's own schedule editor
 * carries (ADR-027). CEO 2026-08-28: a curator template must be able to express
 * everything a client can set by hand, otherwise the expert-authored default is
 * poorer than the self-made one. The auto-schedule's own proposal still stops at
 * three (D-5); that is a property of the ALGORITHM, not of this template.
 * Only the LAST position may be open-ended
 * (ADR-027). An open-ended position in the middle would silently swallow every
 * position after it, which is a schedule the operator did not ask for rather
 * than an error they can see.
 */
export const venueFormatTemplatePartWriteSchema = z
    .array(venueFormatTemplateItemWriteSchema)
    .max(5)
    .superRefine((items, ctx) => {
    items.forEach((item, index) => {
        if (item.duration_minutes === null && index !== items.length - 1) {
            ctx.addIssue({
                code: 'custom',
                path: [index, 'duration_minutes'],
                message: '«До конца слота» допускается только у последней подборки',
            });
        }
    });
});
/** One tariff's whole template — all three parts, empty arrays included. */
export const venueFormatTemplateTariffWriteSchema = z
    .object({
    morning: venueFormatTemplatePartWriteSchema,
    day: venueFormatTemplatePartWriteSchema,
    evening: venueFormatTemplatePartWriteSchema,
})
    .strict();
/**
 * `PUT /admin/settings/venue-formats/:id/schedule-template` body. Both keys are
 * optional — the editor sends only the tabs the curator touched — but at least
 * one must be present.
 *
 * ONE REQUEST, BOTH SETS, ONE TRANSACTION (DV-5). The design anticipated a
 * partial outcome («Standard сохранён, Pro не удалось»); an operation that is
 * genuinely one operation cannot half-succeed, and what a partial save actually
 * produces is the state a two-tab form handles worst — half the screen
 * committed and the curator having to remember which half. So this answers
 * either 200 with both sets fresh, or 4xx/5xx with NOTHING written.
 */
export const venueFormatTemplateWriteSchema = z
    .object({
    standard: venueFormatTemplateTariffWriteSchema.optional(),
    pro: venueFormatTemplateTariffWriteSchema.optional(),
})
    .strict()
    .refine((obj) => obj.standard !== undefined || obj.pro !== undefined, 'At least one tariff set must be provided');
/**
 * Why a stored position can no longer be used. Reported on READ so the curator
 * sees the rot on their own surface; at proposal time the assembly simply skips
 * such a position (§2.8) and the client is never told the template is broken.
 */
export const venueFormatTemplateUnavailableReasonSchema = z.enum([
    'deleted',
    'not_active',
    'not_curated',
    'tier_out_of_window',
]);
export const venueFormatTemplateItemReadSchema = z
    .object({
    position: z.number().int().nonnegative(),
    preset_id: uuidSchema,
    /** `null` only if the preset row was hard-deleted. */
    preset_name: z.string().nullable(),
    preset_status: presetStatusSchema.nullable(),
    preset_tier_access: tierAccessEnum.nullable(),
    duration_minutes: z.number().int().nullable(),
    is_available: z.boolean(),
    unavailable_reason: venueFormatTemplateUnavailableReasonSchema.nullable(),
})
    .strict();
export const venueFormatTemplateTariffBlockSchema = z
    .object({
    morning: z.array(venueFormatTemplateItemReadSchema),
    day: z.array(venueFormatTemplateItemReadSchema),
    evening: z.array(venueFormatTemplateItemReadSchema),
    /** ≥1 position in any part. */
    is_configured: z.boolean(),
    unavailable_count: z.number().int().nonnegative(),
})
    .strict();
/**
 * `GET /admin/settings/venue-formats/:id/schedule-template` — BOTH tariff
 * blocks in one response. US-11 shows «два независимых набора» on one screen,
 * and D-26's «Скопировать из Standard» then needs no endpoint at all: the Pro
 * tab copies from the Standard block in client state and saves through the same
 * `PUT`. The direction is safe by construction — `TIER_WINDOW.standard ⊆
 * TIER_WINDOW.pro` under every configuration, so a Standard-valid position is
 * always Pro-valid. The reverse copy would not be, which is why only one
 * direction is offered.
 */
export const venueFormatTemplateResponseSchema = z
    .object({
    venue_format_id: uuidSchema,
    tariffs: z
        .object({
        standard: venueFormatTemplateTariffBlockSchema,
        pro: venueFormatTemplateTariffBlockSchema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=venue-format.js.map